import { Route, Routes } from 'react-router-dom';

import { BrandFonts } from './BrandFonts';
import { AdminDashboardScreen } from '@/features/admin/DashboardScreen';
import { ClinicalQueueScreen } from '@/features/pharmacist/ClinicalQueueScreen';
import { RolesScreen } from '@/features/admin/RolesScreen';
import { RecallsScreen } from '@/features/admin/RecallsScreen';
import { ProhibitionsScreen } from '@/features/admin/ProhibitionsScreen';
import { AuditDisclosureScreen } from '@/features/admin/AuditDisclosureScreen';
import { AdvertsScreen } from '@/features/seller/AdvertsScreen';
import { ShopFrontScreen } from '@/features/admin/ShopFrontScreen';
import { VerifyPackScreen } from '@/features/buyer/VerifyPackScreen';
import { SponsorshipsScreen } from '@/features/admin/SponsorshipsScreen';
import { ReceivingScreen } from '@/features/seller/ReceivingScreen';
import { SellerRecallsScreen } from '@/features/seller/RecallsScreen';
import { SellerStaffScreen } from '@/features/seller/StaffScreen';
import { SellerDashboardScreen } from '@/features/seller/DashboardScreen';
import { SellerComplaintsScreen } from '@/features/seller/ComplaintsScreen';
import { BuyerLayout } from './BuyerLayout';
import { AdminLayout } from './AdminLayout';
import { AccountScreen } from '@/features/account/AccountScreen';
import { AddressBookScreen } from '@/features/account/AddressBookScreen';
import { SupportScreen } from '@/features/support/SupportScreen';
import { armSound } from '@/lib/chime';
import { SupportDeskScreen } from '@/features/admin/SupportDeskScreen';
import { CollectionsScreen } from '@/features/admin/CollectionsScreen';
import { HandoverScreen } from '@/features/rider/HandoverScreen';
import { ListingsScreen } from '@/features/seller/ListingsScreen';
import { InventoryScreen } from '@/features/seller/InventoryScreen';
import { AddProductScreen } from '@/features/seller/AddProductScreen';
import { OnboardingScreen } from '@/features/seller/OnboardingScreen';
import { PharmacyApprovalScreen } from '@/features/admin/PharmacyApprovalScreen';
import { CatalogueScreen } from '@/features/admin/CatalogueScreen';
import { DeliveryZonesScreen } from '@/features/admin/DeliveryZonesScreen';
import { QuoteBoardScreen } from '@/features/admin/QuoteBoardScreen';
import { SubmissionsScreen } from '@/features/admin/SubmissionsScreen';
import { UsersScreen } from '@/features/admin/UsersScreen';
import { PharmacyRegisterScreen } from '@/features/seller/PharmacyRegisterScreen';
import { MoneyScreen } from '@/features/seller/MoneyScreen';
import { OrderQueueScreen } from '@/features/seller/OrderQueueScreen';
import { RequireRole } from './RequireRole';
import { RunsScreen } from '@/features/rider/RunsScreen';
import { SellerLayout } from './SellerLayout';
import { RefundReviewScreen } from '@/features/admin/RefundReviewScreen';
import { HomeScreen } from '@/features/home/HomeScreen';
import { SearchScreen } from '@/features/search/SearchScreen';
import { ProductScreen } from '@/features/product/ProductScreen';
import { PrescriptionScreen } from '@/features/prescriptions/PrescriptionScreen';
import { QuoteComparisonScreen } from '@/features/prescriptions/QuoteComparisonScreen';
import { QuoteQueueScreen } from '@/features/seller/QuoteQueueScreen';
import { ArticleScreen } from '@/features/content/ArticleScreen';
import { BlogScreen } from '@/features/content/BlogScreen';
import { PageScreen } from '@/features/content/PageScreen';
import { SettingsScreen } from '@/features/admin/SettingsScreen';
import { ShelfLifeScreen } from '@/features/admin/ShelfLifeScreen';
import { AdminBlogScreen } from '@/features/admin/BlogScreen';
import { ContentScreen } from '@/features/admin/ContentScreen';
import { StaffLoginScreen } from '@/features/auth/StaffLoginScreen';
import { ForgotPasswordScreen } from '@/features/auth/ForgotPasswordScreen';
import { SignInScreen } from '@/features/auth/SignInScreen';
import { CartScreen } from '@/features/cart/CartScreen';
import { CheckoutScreen } from '@/features/checkout/CheckoutScreen';
import { OrderScreen } from '@/features/orders/OrderScreen';
import { ReportProblemScreen } from '@/features/orders/ReportProblemScreen';
import { OrdersScreen } from '@/features/orders/OrdersScreen';
import { RequireBuyer } from './RequireBuyer';
import { RequireAdmin } from './RequireAdmin';
import { NotFoundScreen } from './NotFoundScreen';

/**
 * Routes.
 *
 * §6: buyer, seller and admin ship as role-gated route groups in ONE app. The
 * seller group mounts alongside these in Phase 1 without moving anything.
 */

/**
 * Listen for the first interaction, so a notification can make a sound.
 *
 * Browsers refuse to play audio until somebody has touched the page, and
 * that is right: a site that makes a noise before you have touched it is a
 * site people close. Nothing is created until that first tap — see
 * lib/chime.ts.
 */
armSound();

export function App() {
  return (
    <>
      {/* Rewrites the two type variables from the admin's choice. Renders
          nothing and sits outside the routes, because the typeface does not
          belong to any one screen. */}
      <BrandFonts />

      <Routes>
        <Route element={<BuyerLayout />}>
          <Route index element={<HomeScreen />} />
          <Route path="search" element={<SearchScreen />} />
          {/* How to check a pack with NAFDAC, and what the reply said.
              Public: somebody holding a pack they do not trust should not
              have to make an account to find out what to do about it. */}
          <Route path="verify" element={<VerifyPackScreen />} />
          <Route path="products/:slug" element={<ProductScreen />} />

          {/*
          Animal health: the same three screens, behind /vet.

          The PATH is the wall. BuyerLayout reads it, re-skins itself, prints
          the "for animals only" strip and swaps the tab bar — so there is no
          flag to keep in sync, nothing to go stale in a tab left open, and a
          shared link always opens in the shop it belongs to.

          Cart, checkout, orders and account are deliberately NOT duplicated
          here. A farmer with a dog and a headache is one person with one
          account and one order history; only browsing is walled.
        */}
          <Route path="vet" element={<HomeScreen />} />
          <Route path="vet/search" element={<SearchScreen />} />
          <Route path="vet/products/:slug" element={<ProductScreen />} />
          <Route path="vet/blog" element={<BlogScreen />} />
          <Route path="vet/blog/:slug" element={<ArticleScreen />} />
          {/* CMS pages: terms, privacy, returns policy. Public by design — a
            returns policy behind a login is not a policy, it is a trap. */}
          <Route path="pages/:slug" element={<PageScreen />} />

          {/* The blog. Public, and SECTOR-AWARE: /blog is the human shelf and
            /vet/blog the animal-health one. An article on poultry withdrawal
            periods has no business among articles about children's fever, for
            the same reason cattle ivermectin has none in a search for
            "dewormer" (see BlogController). */}
          <Route path="blog" element={<BlogScreen />} />
          <Route path="blog/:slug" element={<ArticleScreen />} />

          {/* ── The prescription board ────────────────────────────────────
            A buyer showing a pharmacist a piece of paper and being told a
            price. Behind a sign-in because a prescription is health data
            (§9.15), and because the replies have to reach somebody.

            Accepting a quote lands on /orders/:reference like any other
            checkout — there is no separate prescription payment screen. */}
          <Route
            path="prescriptions"
            element={
              <RequireBuyer>
                <PrescriptionScreen />
              </RequireBuyer>
            }
          />
          <Route
            path="prescriptions/:reference"
            element={
              <RequireBuyer>
                <QuoteComparisonScreen />
              </RequireBuyer>
            }
          />
          {/* The same board, on the animal-health side of the wall. */}
          <Route
            path="vet/prescriptions"
            element={
              <RequireBuyer>
                <PrescriptionScreen />
              </RequireBuyer>
            }
          />

          {/* The order spine. Cart is open to everyone — a buyer fills one
            before they ever hand over a phone number. */}
          <Route path="cart" element={<CartScreen />} />
          <Route path="sign-in" element={<SignInScreen />} />
          <Route path="account" element={<AccountScreen />} />
          {/* Somewhere to fix an address that is not the middle of a checkout. */}
          <Route
            path="account/addresses"
            element={
              <RequireBuyer>
                <AddressBookScreen />
              </RequireBuyer>
            }
          />
          {/* Live support. One page, not a floating bubble: a widget pinned
              to every screen sits on top of whatever somebody is doing — the
              Add to cart button, on a phone — and polls everywhere whether or
              not anybody opened it.

              PUBLIC, and that is the whole point. The people most likely to
              need support are the ones who cannot get in: somebody whose code
              never arrived, somebody deciding whether to sign up at all, a
              pharmacy locked out of its console. Answering them with a login
              screen answers the wrong question.

              The widget asks for an email or a phone number instead, which is
              what makes these enquiries somebody can follow up. A signed-in
              visitor is recognised anyway and their account attached. */}
          <Route path="support" element={<SupportScreen />} />
          <Route
            path="checkout"
            element={
              <RequireBuyer>
                <CheckoutScreen />
              </RequireBuyer>
            }
          />
          <Route
            path="orders"
            element={
              <RequireBuyer>
                <OrdersScreen />
              </RequireBuyer>
            }
          />
          <Route
            path="orders/:reference"
            element={
              <RequireBuyer>
                <OrderScreen />
              </RequireBuyer>
            }
          />
          {/* §4.5 — reporting a problem with one pharmacy's delivery. Nested
            under the order so the back arrow lands where the buyer came from. */}
          <Route
            path="orders/:reference/report/:subOrder"
            element={
              <RequireBuyer>
                <ReportProblemScreen />
              </RequireBuyer>
            }
          />
          <Route path="*" element={<NotFoundScreen />} />
        </Route>

        {/* Sign-in sits OUTSIDE the admin layout: a login screen wrapped in the
          console chrome shows navigation to someone who cannot use it. */}
        <Route path="admin/login" element={<StaffLoginScreen door="admin" />} />
        <Route path="admin/forgot-password" element={<ForgotPasswordScreen door="admin" />} />

        <Route
          path="admin"
          element={
            <RequireAdmin>
              <AdminLayout />
            </RequireAdmin>
          }
        >
          {/* The console's front page. Settings was never the right one —
              it answers "what did this used to be", not "is ChiCare working". */}
          <Route
            index
            element={
              <RequireAdmin permission="dashboard.read">
                <AdminDashboardScreen />
              </RequireAdmin>
            }
          />

        {/*
          The clinical desk, inside the console rather than beside it.

          A buyer who reports that a medicine made them ill is told a clinician
          will read it within 48 hours; this is the screen that makes that true.
          It is the ONE room a pharmacist may enter — the gate on this route
          admits them, and every other route in the console does not. A menu
          that merely hid the others would be a suggestion about where to look
          rather than a rule about what may be opened.
        */}
        <Route
          path="clinical"
          element={
            <RequireAdmin permission="clinical.read">
              <ClinicalQueueScreen />
            </RequireAdmin>
          }
        />
          <Route
            path="settings"
            element={
              <RequireAdmin permission="settings.edit">
                <SettingsScreen />
              </RequireAdmin>
            }
          />
          <Route
            path="shelf-life"
            element={
              <RequireAdmin permission="shelf_life.edit">
                <ShelfLifeScreen />
              </RequireAdmin>
            }
          />
          <Route
            path="delivery"
            element={
              <RequireAdmin permission="delivery.edit">
                <DeliveryZonesScreen />
              </RequireAdmin>
            }
          />
          <Route
            path="prescriptions"
            element={
              <RequireAdmin permission="prescriptions.read">
                <QuoteBoardScreen />
              </RequireAdmin>
            }
          />
          <Route
            path="pages"
            element={
              <RequireAdmin permission="pages.edit">
                <ContentScreen />
              </RequireAdmin>
            }
          />

          {/* The blog desk. `posts.write` OR `posts.publish`, because an editor
            whose only job is to review and publish holds the second without
            the first. Deliberately NOT `pages.edit`: that permission carries
            the returns policy and the terms, which are promises to buyers and
            to pharmacies, and a content writer should not inherit it. */}
          <Route
            path="blog"
            element={
              <RequireAdmin permission={['posts.write', 'posts.publish']}>
                <AdminBlogScreen />
              </RequireAdmin>
            }
          />
          <Route
            path="refunds"
            element={
              <RequireAdmin permission="refunds.read">
                <RefundReviewScreen />
              </RequireAdmin>
            }
          />
          <Route
            path="collections"
            element={
              <RequireAdmin permission="collections.read">
                <CollectionsScreen />
              </RequireAdmin>
            }
          />
          <Route
            path="pharmacies"
            element={
              <RequireAdmin permission="pharmacies.read">
                <PharmacyApprovalScreen />
              </RequireAdmin>
            }
          />
          <Route
            path="catalogue"
            element={
              <RequireAdmin permission="catalogue.read">
                <CatalogueScreen />
              </RequireAdmin>
            }
          />
          <Route
            path="catalogue/requests"
            element={
              <RequireAdmin permission="catalogue.read">
                <SubmissionsScreen />
              </RequireAdmin>
            }
          />
          <Route
            path="users"
            element={
              <RequireAdmin permission="people.read">
                <UsersScreen />
              </RequireAdmin>
            }
          />
        <Route
            path="roles"
            element={
              <RequireAdmin permission="people.manage">
                <RolesScreen />
              </RequireAdmin>
            }
          />
          <Route
            path="recalls"
            element={
              <RequireAdmin permission="recalls.issue">
                <RecallsScreen />
              </RequireAdmin>
            }
          />
          <Route
            path="withdrawn"
            element={
              <RequireAdmin permission="prohibitions.issue">
                <ProhibitionsScreen />
              </RequireAdmin>
            }
          />
          <Route
            path="support"
            element={
              <RequireAdmin permission="support.handle">
                <SupportDeskScreen />
              </RequireAdmin>
            }
          />
          <Route
            path="disclosures"
            element={
              <RequireAdmin permission="audit.disclose">
                <AuditDisclosureScreen />
              </RequireAdmin>
            }
          />
          <Route
            path="advertising"
            element={
              <RequireAdmin permission="ads.manage">
                <SponsorshipsScreen />
              </RequireAdmin>
            }
          />
          {/* pages.edit, not catalogue.edit: setting a picture on a tile must
              not require the right to change drug facts (§9.1). */}
          <Route
            path="shopfront"
            element={
              <RequireAdmin permission="pages.edit">
                <ShopFrontScreen />
              </RequireAdmin>
            }
          />
        </Route>

        {/*
        The seller console. The same sign-in FORM as the admin console — a
        pharmacist and an operator both hold a password, and running two login
        screens would mean two places to get password reset wrong — but its own
        DOOR, with its own sign and its own landing. Passing no `door` here is
        what used to send a pharmacy owner to /admin to be told they were a
        pharmacy account.
      */}
        <Route path="sell" element={<PharmacyRegisterScreen />} />
        <Route path="seller/login" element={<StaffLoginScreen door="pharmacy" />} />
        <Route path="seller/forgot-password" element={<ForgotPasswordScreen door="pharmacy" />} />
        <Route
          path="seller"
          element={
            <RequireRole role="pharmacy" signInPath="/seller/login">
              <SellerLayout />
            </RequireRole>
          }
        >
          {/* The dashboard, not the queue. A seller opening their console
              wants to know what is waiting AND how the shop is doing, and the
              queue only ever answered the first half. It is still one tap
              away, and the pipeline at the top of the dashboard links
              straight into it. */}
          <Route index element={<SellerDashboardScreen />} />
          <Route path="orders" element={<OrderQueueScreen />} />
          <Route path="adverts" element={<AdvertsScreen />} />
          <Route path="complaints" element={<SellerComplaintsScreen />} />
          <Route path="receiving" element={<ReceivingScreen />} />
          <Route path="recalls" element={<SellerRecallsScreen />} />
          <Route path="team" element={<SellerStaffScreen />} />
          {/* The old address, kept so a bookmark or a notification link from
              before this change still lands somewhere sensible. */}
          <Route path="income" element={<SellerDashboardScreen />} />
          <Route path="onboarding" element={<OnboardingScreen />} />
          <Route path="listings" element={<ListingsScreen />} />
          <Route path="inventory" element={<InventoryScreen />} />
          <Route path="prescriptions" element={<QuoteQueueScreen />} />
          <Route path="add" element={<AddProductScreen />} />
          <Route path="money" element={<MoneyScreen />} />
        </Route>

        {/*
        The rider app. Deliberately has no layout chrome: a rider is on a bike
        and every screen is either a list of addresses or one door.
      */}
        <Route path="rider/login" element={<StaffLoginScreen door="rider" />} />
        <Route path="rider/forgot-password" element={<ForgotPasswordScreen door="rider" />} />
        <Route
          path="rider"
          element={
            <RequireRole role="rider" signInPath="/rider/login">
              <RunsScreen />
            </RequireRole>
          }
        />
        <Route
          path="rider/runs/:reference"
          element={
            <RequireRole role="rider" signInPath="/rider/login">
              <HandoverScreen />
            </RequireRole>
          }
        />
      </Routes>
    </>
  );
}
